import { Suspense, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { createIntegration, updateIntegration } from 'api/integrations';
import * as IntegrationItem from 'assets/js/pages/account-settings/account-integrations/IntegrationItem';
import { ThankViewMapping } from 'assets/js/pages/account-settings/account-integrations/ThankView/ThankViewMapping';
import * as Form from 'components/form';
import { InputCopyButton } from 'components/input';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface ThankViewFormValues {
    client_id: string;
    client_secret: string;
}

const ThankView = () => {
    const { success, error } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const { accountId } = useCurrentAccount();
    const { data: integrations } = useCurrentAccountIntegrations();
    const { thankview } = integrations || {};
    const hasStoredValues = !!thankview?.active;
    const inputRef = useRef<HTMLInputElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    const defaultValues = {
        client_id: thankview?.authentication.client_id || '',
        client_secret: thankview?.authentication.client_secret || '',
    };
    const methods = Form.useGivebutterForm<ThankViewFormValues>({ defaultValues });
    const {
        control,
        formState: { isValid, isDirty },
        handleSubmit,
        path,
        reset,
    } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        ThankViewFormValues
    >({
        mutationFn: ({ client_id, client_secret }) => {
            const payload = {
                name: 'thankview',
                client_id,
                client_secret,
            };
            return hasStoredValues
                ? updateIntegration(accountId, thankview.id, payload)
                : createIntegration(accountId, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.integrations(accountId),
            });
            success('Saved ThankView settings!');
            setIsEditing(false);
        },
        onError: e => {
            error(
                e.response?.data?.message ||
                    'Something went wrong. Please check your Client ID and Secret.',
            );
        },
    });

    return (
        <IntegrationItem.Root>
            <IntegrationItem.Header>
                <IntegrationItem.Logo>
                    <img
                        alt="ThankView"
                        className="max-w-[100px]"
                        src={`/images/logos/thankview.png`}
                    />
                </IntegrationItem.Logo>
                <IntegrationItem.Description>
                    <p className="text-xl font-medium">ThankView</p>
                    <p className="max-w-xs text-base text-gray-700">
                        Combine the best in personalized stewardship and fundraising under one roof
                        to help you raise more money, thank donors faster and track your ROI.
                    </p>

                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://thankview.com/giving"
                    >
                        https://thankview.com/giving
                    </a>
                </IntegrationItem.Description>
                <IntegrationItem.Expander />
            </IntegrationItem.Header>
            <IntegrationItem.Content>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex max-w-lg flex-col items-start gap-4"
                    >
                        <Form.Container>
                            <Form.Label name={path('client_id')}>
                                ThankView Client ID <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                name="client_id"
                                control={control}
                                disabled={!isEditing && hasStoredValues}
                                rules={{ required: 'Client ID is Required.' }}
                            >
                                <Form.TextInputForm
                                    ref={inputRef}
                                    placeholder="Enter your Client ID"
                                />
                                {!isEditing && hasStoredValues && (
                                    <InputCopyButton text={thankview.authentication.public_key} />
                                )}
                            </Form.TextInputRoot>
                            <Form.Error name={path('client_id')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('client_secret')}>
                                ThankView Client Secret <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                name="client_secret"
                                control={control}
                                disabled={!isEditing && hasStoredValues}
                                rules={{ required: 'Client Secret is Required.' }}
                            >
                                <Form.TextInputForm placeholder="Enter your Client Secret" />
                                {!isEditing && hasStoredValues && (
                                    <InputCopyButton text={thankview.authentication.public_key} />
                                )}
                            </Form.TextInputRoot>
                            <Form.Error name={path('client_secret')} />
                        </Form.Container>

                        {hasStoredValues && !isEditing ? (
                            <Button
                                variant="solid"
                                ref={editButtonRef}
                                onClick={() => {
                                    flushSync(() => {
                                        setIsEditing(true);
                                    });
                                    inputRef.current?.select();
                                }}
                            >
                                Edit
                            </Button>
                        ) : (
                            <div className="flex gap-2">
                                <Form.SubmitButton
                                    isSubmitting={isLoading}
                                    disabled={!isDirty || !isValid}
                                >
                                    Save
                                </Form.SubmitButton>
                                {hasStoredValues ? (
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        onClick={() => {
                                            flushSync(() => {
                                                setIsEditing(false);
                                            });
                                            editButtonRef.current?.focus();
                                            reset(defaultValues);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                            </div>
                        )}
                    </form>
                </Form.Provider>

                {hasStoredValues ? (
                    <Suspense
                        fallback={
                            <div className="flex justify-center p-4">
                                <Loader variant="static" />
                            </div>
                        }
                    >
                        <Separator />
                        <ThankViewMapping />
                    </Suspense>
                ) : null}
            </IntegrationItem.Content>
        </IntegrationItem.Root>
    );
};

export { ThankView };
